// Settings, Notifications: phone notifications for partner events (web push). The state comes
// from the server (GET /api/push) and from this device (support, Home Screen, permission); the
// page shows one clear state and what to do next. Pure: main.ts gathers the device facts.
import type { OverviewDto, PushViewDto } from '../../../shared/api';
import {
  NOTIFY_DENIED,
  NOTIFY_FALLBACK,
  NOTIFY_INTRO,
  NOTIFY_NOT_INSTALLED,
  NOTIFY_OFF,
  NOTIFY_ON,
  NOTIFY_SERVER_OFF,
  NOTIFY_TITLE,
  NOTIFY_UNSUPPORTED,
  PUSH_KIND_LABEL,
  PUSH_KINDS,
} from '../../../shared/push';
import { esc, shortDate } from '../format';
import { detailBar } from './header';

/** What this device can do, read in main.ts (never stored on the server). */
export interface DeviceFacts {
  /** Service worker, PushManager and Notification all exist. */
  supported: boolean;
  /** Opened from the Home Screen (display-mode standalone). */
  standalone: boolean;
  /** iPhone or iPad: push needs the Home Screen app there. */
  ios: boolean;
  permission: 'default' | 'granted' | 'denied';
}

export interface PushUi {
  server: PushViewDto | null;
  device: DeviceFacts;
  /** This device's id on the server (localStorage), when it has a live subscription. */
  deviceId: string | null;
  busy: boolean;
}

export type PushState = 'loading' | 'server-off' | 'not-installed' | 'unsupported' | 'denied' | 'on' | 'off';

export function pushState(p: PushUi | null): PushState {
  if (!p?.server) return 'loading';
  if (!p.server.enabled) return 'server-off';
  if (p.device.ios && !p.device.standalone) return 'not-installed';
  if (!p.device.supported) return 'unsupported';
  if (p.device.permission === 'denied') return 'denied';
  return p.deviceId && p.server.devices.some((d) => d.id === p.deviceId) ? 'on' : 'off';
}

/** The status on the Settings menu row. */
export const pushMenuStatus = (p: PushUi | null): string =>
  ({ loading: '', 'server-off': 'Email only', 'not-installed': 'Add to Home Screen', unsupported: 'Email only', denied: 'Blocked', on: 'On', off: 'Off' })[pushState(p)];

export function vNotifications(_d: OverviewDto, p: PushUi | null): string {
  const head = detailBar(NOTIFY_TITLE, '');
  const state = pushState(p);
  if (state === 'loading') return `${head}<p class="empty">Loading...</p>`;
  const busy = p!.busy ? ' disabled' : '';
  const hint = (t: string) => `<p class="hint">${esc(t)}</p>`;
  const body: Record<Exclude<PushState, 'loading'>, string> = {
    'server-off': hint(NOTIFY_SERVER_OFF),
    'not-installed': hint(NOTIFY_NOT_INSTALLED),
    unsupported: hint(NOTIFY_UNSUPPORTED),
    denied: hint(NOTIFY_DENIED),
    off: `${hint(NOTIFY_OFF)}<div class="btns"><button class="btn primary" data-a="push-enable"${busy}>Allow notifications</button></div>`,
    on: `${hint(NOTIFY_ON)}<div class="btns"><button class="btn" data-a="push-test"${busy}>Send a test notification</button><button class="btn" data-a="push-disable"${busy}>Turn off on this device</button></div>`,
  };
  const s = p!.server!;
  const kinds =
    state === 'on'
      ? `<h2>Notify me about</h2><div class="list">${PUSH_KINDS.map(
          (k) => `<label class="pkind"><input type="checkbox" data-push-kind="${k}"${s.kinds[k] ? ' checked' : ''}${busy}> ${esc(PUSH_KIND_LABEL[k])}</label>`,
        ).join('')}</div>`
      : '';
  const others = s.devices.filter((d) => d.id !== p!.deviceId);
  const devices = others.length
    ? `<h2>Other devices</h2><div class="list">${others
        .map(
          (d) =>
            `<div class="pdev"><span>Added ${esc(shortDate(d.createdAt.slice(0, 10)))}${d.failing ? ' <small>Not reached lately</small>' : ''}</span><button class="btn" data-a="push-remove" data-id="${esc(d.id)}"${busy}>Turn off</button></div>`,
        )
        .join('')}</div>`
    : '';
  return `${head}<p>${esc(NOTIFY_INTRO)}</p>${body[state]}${kinds}${devices}${hint(NOTIFY_FALLBACK)}`;
}
